import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    ArrowLeft,
    MapPin,
    Briefcase,
    Mail,
    Phone,
    MessageSquare,
    User,
    Calendar,
    GraduationCap,
    Heart,
    Users
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberViewProfilePage = () => {
    const { memberId } = useParams();
    const navigate = useNavigate();
    const { member: currentMember } = useAuth();
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMember();
    }, [memberId]);

    const fetchMember = async () => {
        try {
            const response = await axios.get(`${API}/member/directory/${memberId}`);
            setMember(response.data);
        } catch (error) {
            console.error('Error fetching member:', error);
            toast.error('Failed to load profile');
            navigate('/member/directory');
        } finally {
            setLoading(false);
        }
    };

    const startConversation = async () => {
        try {
            const response = await axios.post(`${API}/member/conversations`, {
                participant_id: memberId
            });
            navigate('/member/messages', { state: { conversationId: response.data.id } });
        } catch (error) {
            toast.error('Failed to start conversation');
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getProfileImageUrl = (profileImage) => {
        if (!profileImage) return null;
        if (profileImage.startsWith('http')) return profileImage;
        if (profileImage.startsWith('/api/uploads')) {
            return `${process.env.REACT_APP_BACKEND_URL}${profileImage}`;
        }
        if (profileImage.startsWith('/uploads')) {
            return `${process.env.REACT_APP_BACKEND_URL}/api${profileImage}`;
        }
        return profileImage;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-[#A0A0A0] animate-pulse">Loading profile...</div>
            </div>
        );
    }

    if (!member) {
        return (
            <div className="text-center py-12">
                <p className="text-[#A0A0A0]">Member not found</p>
                <Button
                    onClick={() => navigate('/member/directory')}
                    className="mt-4"
                >
                    Back to Directory
                </Button>
            </div>
        );
    }

    const profileImageUrl = getProfileImageUrl(member.profile_image);

    return (
        <div className="space-y-6 max-w-4xl mx-auto">
            {/* Back Button */}
            <Button
                variant="ghost"
                onClick={() => navigate('/member/directory')}
                className="text-[#A0A0A0] hover:text-white"
            >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Directory
            </Button>

            {/* Profile Header Card */}
            <Card className="border-[#5A6350] overflow-hidden">
                <div className="h-32 bg-gradient-to-r from-[#3D4435] to-[#4A5240]" />
                <CardContent className="relative pt-0 pb-6">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-16">
                        {/* Profile Image */}
                        <div className="relative">
                            {profileImageUrl ? (
                                <>
                                    <img
                                        src={profileImageUrl}
                                        alt={member.name}
                                        className="w-32 h-32 rounded-xl object-cover border-4 border-[#4A5240] bg-[#3D4435]"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                    <div 
                                        className="w-32 h-32 rounded-xl bg-[#C9A227] items-center justify-center text-[#3D4435] text-3xl font-bold border-4 border-[#4A5240] hidden"
                                    >
                                        {getInitials(member.name)}
                                    </div>
                                </>
                            ) : (
                                <div className="w-32 h-32 rounded-xl bg-[#C9A227] flex items-center justify-center text-[#3D4435] text-3xl font-bold border-4 border-[#4A5240]">
                                    {getInitials(member.name)}
                                </div>
                            )}
                        </div>

                        {/* Name and Basic Info */}
                        <div className="flex-1 sm:pb-2">
                            <h1 className="text-2xl font-bold text-white font-['Playfair_Display']">
                                {member.name}
                            </h1>
                            <p className="text-[#C9A227]">@{member.username}</p>
                            {member.profession && (
                                <p className="text-[#A0A0A0] flex items-center gap-2 mt-1">
                                    <Briefcase className="w-4 h-4" />
                                    {member.profession}
                                </p>
                            )}
                        </div>

                        {/* Action Buttons */}
                        {memberId !== currentMember?.id && (
                            <Button
                                onClick={startConversation}
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                            >
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Send Message
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Profile Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* About Section */}
                {member.bio && (
                    <Card className="border-[#5A6350] md:col-span-2">
                        <CardContent className="p-6">
                            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                <User className="w-5 h-5 text-[#C9A227]" />
                                About
                            </h2>
                            <p className="text-[#A0A0A0] whitespace-pre-wrap">{member.bio}</p>
                        </CardContent>
                    </Card>
                )}

                {/* Contact & Location */}
                <Card className="border-[#5A6350]">
                    <CardContent className="p-6">
                        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-[#C9A227]" />
                            Contact & Location
                        </h2>
                        <div className="space-y-3">
                            {member.location && (
                                <div className="flex items-center gap-3 text-[#A0A0A0]">
                                    <MapPin className="w-4 h-4 text-[#5A6350]" />
                                    <span>{member.location}</span>
                                </div>
                            )}
                            {member.birth_city && (
                                <div className="flex items-center gap-3 text-[#A0A0A0]">
                                    <MapPin className="w-4 h-4 text-[#5A6350]" />
                                    <span>From: {member.birth_city}</span>
                                </div>
                            )}
                            {member.privacy_settings?.show_email !== false && member.email && (
                                <div className="flex items-center gap-3 text-[#A0A0A0]">
                                    <Mail className="w-4 h-4 text-[#5A6350]" />
                                    <span>{member.email}</span>
                                </div>
                            )}
                            {member.privacy_settings?.show_phone !== false && member.phone && (
                                <div className="flex items-center gap-3 text-[#A0A0A0]">
                                    <Phone className="w-4 h-4 text-[#5A6350]" />
                                    <span>{member.phone}</span>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Professional Info */}
                <Card className="border-[#5A6350]">
                    <CardContent className="p-6">
                        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-[#C9A227]" />
                            Professional
                        </h2>
                        <div className="space-y-3">
                            {member.profession && (
                                <div className="flex items-center gap-3 text-[#A0A0A0]">
                                    <Briefcase className="w-4 h-4 text-[#5A6350]" />
                                    <span>{member.profession}</span>
                                </div>
                            )}
                            {member.job_title && (
                                <div className="flex items-center gap-3 text-[#A0A0A0]">
                                    <User className="w-4 h-4 text-[#5A6350]" />
                                    <span>{member.job_title}</span>
                                </div>
                            )}
                            {member.employer && (
                                <div className="flex items-center gap-3 text-[#A0A0A0]">
                                    <Users className="w-4 h-4 text-[#5A6350]" />
                                    <span>{member.employer}</span>
                                </div>
                            )}
                            {member.education && (
                                <div className="flex items-center gap-3 text-[#A0A0A0]">
                                    <GraduationCap className="w-4 h-4 text-[#5A6350]" />
                                    <span>{member.education}</span>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Interests */}
                {member.interests?.length > 0 && (
                    <Card className="border-[#5A6350]">
                        <CardContent className="p-6">
                            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <Heart className="w-5 h-5 text-[#C9A227]" />
                                Interests
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                {member.interests.map((interest, idx) => (
                                    <Badge 
                                        key={idx}
                                        className="bg-[#3D4435] text-[#A0A0A0] border border-[#5A6350]"
                                    >
                                        {interest}
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Hobbies */}
                {member.hobbies?.length > 0 && (
                    <Card className="border-[#5A6350]">
                        <CardContent className="p-6">
                            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-[#C9A227]" />
                                Hobbies
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                {member.hobbies.map((hobby, idx) => (
                                    <Badge 
                                        key={idx}
                                        className="bg-[#3D4435] text-[#A0A0A0] border border-[#5A6350]"
                                    >
                                        {hobby}
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Member Since */}
            <div className="text-center text-[#5A6350] text-sm">
                Member since {new Date(member.join_date).toLocaleDateString('en-US', { 
                    month: 'long', 
                    year: 'numeric' 
                })}
            </div>
        </div>
    );
};

export default MemberViewProfilePage;
